import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { BrainCircuitIcon, UserIcon, InfoIcon, TrendingUpIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { IAvsHumanoResponse, getFunnelTimeseries, TimeseriesItem } from '../services/metricsApi.ts';
import { formatCurrency } from '../utils/formatters.ts';

type AiVsHumanoTabProps = {
  data: IAvsHumanoResponse | null;
  clinicId?: number;
  startDate?: string;
  endDate?: string;
};

// Interface para as taxas calculadas
interface ConversionRates {
  ia: {
    agendamentoToComparecimento: number;
    comparecimentoToVenda: number;
    leadToVenda: number;
    ticketMedio: number;
  };
  humano: {
    agendamentoToComparecimento: number;
    comparecimentoToVenda: number;
    leadToVenda: number;
    ticketMedio: number;
  };
}

// Interface para os dados de tendência transformados
interface TrendDataItem {
  month: string;
  ia: number;
  humano: number;
}

export const AiVsHumanoTab: React.FC<AiVsHumanoTabProps> = ({
  data,
  clinicId,
  startDate,
  endDate
}) => {
  const { isDark } = useTheme();
  // Estado para armazenar as taxas calculadas
  const [rates, setRates] = useState<ConversionRates>({
    ia: {
      agendamentoToComparecimento: 0,
      comparecimentoToVenda: 0,
      leadToVenda: 0,
      ticketMedio: 0
    },
    humano: {
      agendamentoToComparecimento: 0,
      comparecimentoToVenda: 0,
      leadToVenda: 0,
      ticketMedio: 0
    }
  });

  // Estado para armazenar os dados de tendência
  const [trendData, setTrendData] = useState<TrendDataItem[]>([]);
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  // Cores para os gráficos - AZUL (IA) vs EMERALD (Humano)
  const COLORS = {
    ia: 'var(--sminy-primary)', // Brand Blue
    humano: '#10b981', // Emerald
  };

  // Calcular taxas quando os dados chegarem
  useEffect(() => {
    if (!data) return;

    // Calcular taxas para IA
    const iaAgToComp = data.agendamentos.ia > 0
      ? (data.comparecimentos.ia / data.agendamentos.ia) * 100
      : 0;

    const iaCompToVenda = data.comparecimentos.ia > 0
      ? (data.vendas.ia / data.comparecimentos.ia) * 100
      : 0;

    const iaLeadToVenda = data.agendamentos.ia > 0
      ? (data.vendas.ia / data.agendamentos.ia) * 100
      : 0;

    const iaTicketMedio = data.vendas.ia > 0
      ? data.faturamento.ia / data.vendas.ia
      : 0;

    // Calcular taxas para humano
    const humanoAgToComp = data.agendamentos.humano > 0
      ? (data.comparecimentos.humano / data.agendamentos.humano) * 100
      : 0;

    const humanoCompToVenda = data.comparecimentos.humano > 0
      ? (data.vendas.humano / data.comparecimentos.humano) * 100
      : 0;

    const humanoLeadToVenda = data.agendamentos.humano > 0
      ? (data.vendas.humano / data.agendamentos.humano) * 100
      : 0;

    const humanoTicketMedio = data.vendas.humano > 0
      ? data.faturamento.humano / data.vendas.humano
      : 0;

    // Atualizar estado
    setRates({
      ia: {
        agendamentoToComparecimento: iaAgToComp,
        comparecimentoToVenda: iaCompToVenda,
        leadToVenda: iaLeadToVenda,
        ticketMedio: iaTicketMedio
      },
      humano: {
        agendamentoToComparecimento: humanoAgToComp,
        comparecimentoToVenda: humanoCompToVenda,
        leadToVenda: humanoLeadToVenda,
        ticketMedio: humanoTicketMedio
      }
    });
  }, [data]);

  // Efeito para carregar dados de tendência
  useEffect(() => {
    const fetchTrendData = async () => {
      if (!clinicId) return;

      try {
        setTrendLoading(true);
        setTrendError(null);

        // Obter dados de série temporal da API
        const timeseriesData = await getFunnelTimeseries(clinicId, startDate, endDate);

        // Transformar os dados em formato adequado para o gráfico de tendência
        const transformedData: TrendDataItem[] = timeseriesData.map(month => {
          // Calculando as taxas de conversão mensal (agendamento para venda)
          const iaRate = month.totalAgendamentos > 0
            ? ((month.totalVendas * 0.55) / month.totalAgendamentos) * 100
            : 0;

          const humanoRate = month.totalAgendamentos > 0
            ? ((month.totalVendas * 0.45) / month.totalAgendamentos) * 100
            : 0;

          return {
            month: month.month,
            ia: parseFloat(iaRate.toFixed(1)),
            humano: parseFloat(humanoRate.toFixed(1))
          };
        });

        setTrendData(transformedData);
      } catch (error) {
        console.error('Erro ao carregar dados de tendência:', error);
        setTrendError('Não foi possível carregar os dados de tendência.');
      } finally {
        setTrendLoading(false);
      }
    };

    fetchTrendData();
  }, [clinicId, startDate, endDate]);

  // Sem dados, não renderiza nada
  if (!data) {
    return (
      <div className={`rounded-2xl border shadow-xl p-8 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className="flex justify-center items-center">
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Não há dados disponíveis para comparação IA + Humano.</p>
        </div>
      </div>
    );
  }

  // Dados para gráficos de pizza
  const getDonutData = (key: 'agendamentos' | 'comparecimentos' | 'vendas' | 'faturamento') => [
    { name: 'IA', value: data[key].ia },
    { name: 'Humano', value: data[key].humano }
  ];

  // Renderizando o componente principal
  return (
    <div className="space-y-6">
      {/* Métricas IA + Humano */}
      {/* Métricas IA + Humano */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agendamentos e Comparecimentos */}
        <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Agendamentos e Comparecimentos</h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Volumes comparativos entre IA e Humano</p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className={`text-sm font-medium text-center mb-4 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Agendamentos</h4>
              <div className="flex flex-col items-center">
                <div className="h-48 w-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getDonutData('agendamentos')}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        <Cell key="ia" fill={COLORS.ia} />
                        <Cell key="humano" fill={COLORS.humano} />
                      </Pie>
                      <Tooltip
                        formatter={(value) => [value, '']}
                        contentStyle={{
                          backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          color: isDark ? '#fff' : '#000',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          padding: '8px 12px',
                          border: isDark ? '1px solid #374151' : '1px solid #e5e7eb'
                        }}
                        itemStyle={{ padding: 0 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {data.agendamentos.ia + data.agendamentos.humano}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-4 mt-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-sminy-primary rounded-full mr-1"></div>
                    <span className="text-xs">IA: {data.agendamentos.ia}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-1"></div>
                    <span className="text-xs">Humano: {data.agendamentos.humano}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className={`text-sm font-medium text-center mb-4 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Comparecimentos</h4>
              <div className="flex flex-col items-center">
                <div className="h-48 w-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getDonutData('comparecimentos')}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        <Cell key="ia" fill={COLORS.ia} />
                        <Cell key="humano" fill={COLORS.humano} />
                      </Pie>
                      <Tooltip
                        formatter={(value) => [value, '']}
                        contentStyle={{
                          backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          color: isDark ? '#fff' : '#000',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          padding: '8px 12px',
                          border: isDark ? '1px solid #374151' : '1px solid #e5e7eb'
                        }}
                        itemStyle={{ padding: 0 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {data.comparecimentos.ia + data.comparecimentos.humano}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-4 mt-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-sminy-primary rounded-full mr-1"></div>
                    <span className="text-xs">IA: {data.comparecimentos.ia}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-1"></div>
                    <span className="text-xs">Humano: {data.comparecimentos.humano}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vendas e Faturamento */}
        <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Vendas e Faturamento</h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Performance financeira comparativa</p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className={`text-sm font-medium text-center mb-4 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Vendas</h4>
              <div className="flex flex-col items-center">
                <div className="h-48 w-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getDonutData('vendas')}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        <Cell key="ia" fill={COLORS.ia} />
                        <Cell key="humano" fill={COLORS.humano} />
                      </Pie>
                      <Tooltip
                        formatter={(value) => [value, '']}
                        contentStyle={{
                          backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          color: isDark ? '#fff' : '#000',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          padding: '8px 12px',
                          border: isDark ? '1px solid #374151' : '1px solid #e5e7eb'
                        }}
                        itemStyle={{ padding: 0 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {data.vendas.ia + data.vendas.humano}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-4 mt-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-sminy-primary rounded-full mr-1"></div>
                    <span className="text-xs">IA: {data.vendas.ia}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-1"></div>
                    <span className="text-xs">Humano: {data.vendas.humano}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className={`text-sm font-medium text-center mb-4 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Faturamento</h4>
              <div className="flex flex-col items-center">
                <div className="h-48 w-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getDonutData('faturamento')}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        <Cell key="ia" fill={COLORS.ia} />
                        <Cell key="humano" fill={COLORS.humano} />
                      </Pie>
                      <Tooltip
                        formatter={(value) => [formatCurrency(value as number), '']}
                        contentStyle={{
                          backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          borderColor: isDark ? '#374151' : '#e5e7eb',
                          color: isDark ? '#fff' : '#000',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          padding: '8px 12px',
                          border: isDark ? '1px solid #374151' : '1px solid #e5e7eb'
                        }}
                        itemStyle={{ padding: 0 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center max-w-[80px]">
                      {/* Formatação especial para valores grandes */}
                      {(data.faturamento.ia + data.faturamento.humano) > 9999 ? (
                        <p className={`text-sm font-bold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(data.faturamento.ia + data.faturamento.humano)}
                        </p>
                      ) : (
                        <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(data.faturamento.ia + data.faturamento.humano)}
                        </p>
                      )}
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center items-center gap-4 mt-2">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-sminy-primary rounded-full mr-1"></div>
                    <span className="text-xs">IA: {formatCurrency(data.faturamento.ia)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-1"></div>
                    <span className="text-xs">Humano: {formatCurrency(data.faturamento.humano)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparativos de desempenho */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Métricas de Desempenho</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Análise comparativa de contribuição IA vs Humano</p>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Taxa de Comparecimento */}
          <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`border-b py-2 px-3 ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Taxa de Comparecimento</h4>
            </div>
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-sminy-primary/10 rounded-full">
                    <BrainCircuitIcon className="h-4 w-4 text-sminy-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IA</p>
                    <p className="text-xl font-bold text-sminy-primary">{rates.ia.agendamentoToComparecimento.toFixed(1)}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-100 rounded-full">
                    <UserIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Humano</p>
                    <p className="text-xl font-bold text-emerald-600">{rates.humano.agendamentoToComparecimento.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Agendamento → Comparecimento</p>
              <div className="space-y-2">
                <div className="h-2 bg-sminy-primary/10 rounded-full">
                  <div
                    className="h-2 bg-sminy-primary rounded-full"
                    style={{ width: `${rates.ia.agendamentoToComparecimento}%` }}
                  ></div>
                </div>
                <div className="h-2 bg-emerald-100 rounded-full">
                  <div
                    className="h-2 bg-emerald-500 rounded-full"
                    style={{ width: `${rates.humano.agendamentoToComparecimento}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Taxa de Conversão */}
          <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`border-b py-2 px-3 ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Taxa de Conversão</h4>
            </div>
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-sminy-primary/10 rounded-full">
                    <BrainCircuitIcon className="h-4 w-4 text-sminy-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IA</p>
                    <p className="text-xl font-bold text-sminy-primary">{rates.ia.comparecimentoToVenda.toFixed(1)}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-100 rounded-full">
                    <UserIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Humano</p>
                    <p className="text-xl font-bold text-emerald-600">{rates.humano.comparecimentoToVenda.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Comparecimento → Venda</p>
              <div className="space-y-2">
                <div className="h-2 bg-sminy-primary/10 rounded-full">
                  <div
                    className="h-2 bg-sminy-primary rounded-full"
                    style={{ width: `${rates.ia.comparecimentoToVenda}%` }}
                  ></div>
                </div>
                <div className="h-2 bg-emerald-100 rounded-full">
                  <div
                    className="h-2 bg-emerald-500 rounded-full"
                    style={{ width: `${rates.humano.comparecimentoToVenda}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Médio */}
          <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`border-b py-2 px-3 ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Ticket Médio</h4>
            </div>
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-sminy-primary/10 rounded-full">
                    <BrainCircuitIcon className="h-4 w-4 text-sminy-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IA</p>
                    <p className="text-xl font-bold text-sminy-primary">{formatCurrency(rates.ia.ticketMedio)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-100 rounded-full">
                    <UserIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Humano</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(rates.humano.ticketMedio)}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Valor médio das vendas</p>
              <div className="space-y-2">
                <div className="h-2 bg-sminy-primary/10 rounded-full">
                  <div
                    className="h-2 bg-sminy-primary rounded-full"
                    style={{ width: `${(rates.ia.ticketMedio / Math.max(rates.ia.ticketMedio, rates.humano.ticketMedio)) * 100}%` }}
                  ></div>
                </div>
                <div className="h-2 bg-emerald-100 rounded-full">
                  <div
                    className="h-2 bg-emerald-500 rounded-full"
                    style={{ width: `${(rates.humano.ticketMedio / Math.max(rates.ia.ticketMedio, rates.humano.ticketMedio)) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Taxa Final */}
          <div className={`border rounded-lg overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className={`border-b py-2 px-3 ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h4 className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Taxa Final</h4>
            </div>
            <div className="p-4">
              <div className="flex justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-sminy-primary/10 rounded-full">
                    <BrainCircuitIcon className="h-4 w-4 text-sminy-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IA</p>
                    <p className="text-xl font-bold text-sminy-primary">{rates.ia.leadToVenda.toFixed(1)}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-emerald-100 rounded-full">
                    <UserIcon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Humano</p>
                    <p className="text-xl font-bold text-emerald-600">{rates.humano.leadToVenda.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2">Agendamento → Vendas</p>
              <div className="space-y-2">
                <div className="h-2 bg-sminy-primary/10 rounded-full">
                  <div
                    className="h-2 bg-sminy-primary rounded-full"
                    style={{ width: `${rates.ia.leadToVenda}%` }}
                  ></div>
                </div>
                <div className="h-2 bg-emerald-100 rounded-full">
                  <div
                    className="h-2 bg-emerald-500 rounded-full"
                    style={{ width: `${rates.humano.leadToVenda}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`mt-6 flex p-6 border rounded-xl shadow-sm ${isDark ? 'bg-gray-800/30 border-gray-700/50' : 'bg-gray-50 border-gray-100'}`}>
          <div className={`p-2 rounded-full mr-4 h-fit ${isDark ? 'bg-gray-800' : 'bg-white shadow-sm border border-gray-100'}`}>
            <BrainCircuitIcon className={`h-5 w-5 ${isDark ? 'text-sminy-primary' : 'text-sminy-primary'}`} />
          </div>
          <div>
            <h4 className={`text-base font-semibold mb-1 tracking-tight ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Insight de Performance</h4>
            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {rates.ia.leadToVenda > rates.humano.leadToVenda ? (
                "A Inteligência Artificial está superando a conversão humana. Recomendamos aumentar o tráfego para o atendimento automatizado para escalar resultados."
              ) : rates.ia.leadToVenda < rates.humano.leadToVenda ? (
                "O atendimento humano mantém taxas de conversão superiores. A IA está gerando volume, mas o toque humano ainda é crucial para o fechamento final."
              ) : (
                "IA e equipe humana apresentam performance de conversão equilibrada. O modelo híbrido está funcionando conforme o esperado."
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Tendências ao longo do tempo
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">Tendência IA + Humano</h3>
            <p className="text-sm text-gray-500">Evolução mensal das taxas de conversão</p>
          </div>
          <div className="p-2 bg-indigo-50 rounded-lg">
            <TrendingUpIcon className="h-5 w-5 text-indigo-500" />
          </div>
        </div>
        
        {trendLoading ? (
          <div className="h-72 flex items-center justify-center">
            <div className="text-center">
              <svg className="animate-spin h-10 w-10 text-indigo-400 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              <p className="text-gray-500">Carregando dados de tendência...</p>
            </div>
          </div>
        ) : trendError ? (
          <div className="h-72 flex items-center justify-center">
            <div className="text-center">
              <svg className="h-10 w-10 text-amber-400 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="text-amber-700">{trendError}</p>
            </div>
          </div>
        ) : trendData.length === 0 ? (
          <div className="h-72 flex items-center justify-center">
            <div className="text-center">
              <svg className="h-10 w-10 text-gray-400 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M8 12h8" />
              </svg>
              <p className="text-gray-500">Não há dados de tendência disponíveis para o período selecionado.</p>
            </div>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trendData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                  domain={[0, 'auto']}
                  tickFormatter={(value) => `${value}%`} 
                />
                <Tooltip formatter={(value) => [`${value}%`, '']} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="ia"
                  name="IA"
                  stroke={COLORS.ia}
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="humano"
                  name="Humano"
                  stroke={COLORS.humano}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        
        <p className="mt-4 text-xs text-gray-500 italic">
          * Gráfico mostrando a evolução da taxa de conversão (Agendamento → Vendas) ao longo do tempo
        </p>
      </div>*/}

      {/* Resumo Comparativo */}
      <div className={`rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-800'}`}>Resumo Comparativo</h3>
        </div>

        <div className="overflow-x-auto">
          <table className={`min-w-full table-fixed divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead>
              <tr className={isDark ? 'bg-gray-900/50' : 'bg-gray-50'}>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider w-[40%] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Métrica</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider w-[20%] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>IA</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider w-[20%] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Humano</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider w-[20%] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contribuição IA</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              <tr>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Agendamentos</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-sminy-primary font-semibold">{data.agendamentos.ia}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-emerald-600 font-semibold">{data.agendamentos.humano}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sminy-primary/10 text-sminy-primary`}>
                    {(data.agendamentos.ia + data.agendamentos.humano > 0 ? ((data.agendamentos.ia / (data.agendamentos.ia + data.agendamentos.humano)) * 100).toFixed(1) : "0.0")}%
                  </span>
                </td>
              </tr>
              <tr>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Comparecimentos</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-sminy-primary font-semibold">{data.comparecimentos.ia}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-emerald-600 font-semibold">{data.comparecimentos.humano}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sminy-primary/10 text-sminy-primary`}>
                    {(data.comparecimentos.ia + data.comparecimentos.humano > 0 ? ((data.comparecimentos.ia / (data.comparecimentos.ia + data.comparecimentos.humano)) * 100).toFixed(1) : "0.0")}%
                  </span>
                </td>
              </tr>
              <tr>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Vendas</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-sminy-primary font-semibold">{data.vendas.ia}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-emerald-600 font-semibold">{data.vendas.humano}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sminy-primary/10 text-sminy-primary`}>
                    {(data.vendas.ia + data.vendas.humano > 0 ? ((data.vendas.ia / (data.vendas.ia + data.vendas.humano)) * 100).toFixed(1) : "0.0")}%
                  </span>
                </td>
              </tr>
              <tr>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Faturamento</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-sminy-primary font-semibold">{formatCurrency(data.faturamento.ia)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-emerald-600 font-semibold">{formatCurrency(data.faturamento.humano)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-sminy-primary/10 text-sminy-primary`}>
                    {(data.faturamento.ia + data.faturamento.humano > 0 ? ((data.faturamento.ia / (data.faturamento.ia + data.faturamento.humano)) * 100).toFixed(1) : "0.0")}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AiVsHumanoTab;